import { useState, useCallback } from 'react';
import useConnectWallet from '@/shared/context/WalletProvider';
import { walletAuth } from '@/apiServices';
import { CHAIN_TYPES } from '@/shared/dexUtils';

function generateNonce() {
  return Date.now().toString(); // gets epoch milliseconds of current time
}

export function useWalletAuth() {
  const [errorMessage, setErrorMessage] = useState('');

  // Sign message with Ethereum wallet
  const signWithEthereum = async (wallet) => {
    const nonce = generateNonce();
    const message = `Sign in to Tread with address: ${wallet.address} and nonce: ${nonce}`;

    const signature = await wallet.provider.request({
      method: 'personal_sign',
      params: [message, wallet.address],
    });
    return { signature, message, nonce };
  };

  // Sign message with Solana wallet
  const signWithSolana = async (wallet) => {
    // Create a stable message with nonce
    const nonce = generateNonce();
    const message = `Sign in to Tread with address: ${wallet.address} and nonce: ${nonce}`;

    // Encode message
    const encodedMessage = new TextEncoder().encode(message);

    // Request signature from Phantom wallet
    const { signature } = await wallet.provider.signMessage(encodedMessage, 'utf8');

    // Convert signature to hex string without using Buffer
    const signatureHex = Array.from(signature)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return {
      signature: signatureHex,
      message,
      nonce,
    };
  };

  // Connect and sign in with user-selected chain
  const signInWithWallet = useCallback(async (wallet) => {
    if (!wallet) {
      setErrorMessage('Please select a wallet');
      return false;
    }

    try {
      let signatureData;

      if (wallet.chainType === CHAIN_TYPES.ETHEREUM) {
        signatureData = await signWithEthereum(wallet);
      } else if (wallet.chainType === CHAIN_TYPES.SOLANA) {
        signatureData = await signWithSolana(wallet);
      } else {
        throw new Error('Unsupported chain type');
      }

      // Get referral code from localStorage
      const referralCode = localStorage.getItem('referralCode') || null;

      const result = await walletAuth(
        wallet.address,
        signatureData.signature,
        signatureData.message,
        wallet.chainType,
        referralCode
      );

      // Clear referral code from localStorage after successful authentication if user was created
      if (result.user_created && referralCode) {
        localStorage.removeItem('referralCode');
      }

      return true;
    } catch (error) {
      console.error('Wallet auth error:', error);

      if (!window.ethereum && !window.phantom) {
        setErrorMessage('No wallet detected. Please install MetaMask or Phantom.');
      } else if (error.message.includes('user rejected action')) {
        setErrorMessage('Signature request declined.');
      } else {
        setErrorMessage(error.message || 'Failed to authenticate with wallet');
      }

      return false;
    }
  }, []);

  return {
    signInWithWallet,
    walletAuthError: errorMessage,
  };
}
